/**
 * Step 6: Save & Play
 *
 * Final step: Name the quiz, add description, select category, and save.
 * Shows configuration summary and starts the quiz.
 */

import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { getFeatureIdentifier } from "@/utils/geojsonLoader";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  useStudioStore,
  selectDataset,
  selectPromptKeys,
  selectFilters,
  selectScopeConfig,
  setSaveMetadata,
  resetWizard,
} from "@/stores/useStudioStore";
import { applyFilters, countFilteredFeatures } from "@/utils/filterUtils";
import { buildQuizConfig } from "@/utils/quizConfig";
import { countFinalPrompts } from "@/utils/promptGrouping";
import { db } from "@/db/database";
import type { Category, DatasetSource } from "@/db/types";
import { Save } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

/**
 * Detect category from dataset topics
 */
function detectCategory(topics?: string[]): Category {
  // No topics = uploaded/URL dataset without metadata
  if (!topics || topics.length === 0) return "custom";

  const topicsLower = topics.map((t) => t.toLowerCase());

  if (topicsLower.some((t) => t.includes("phone") || t.includes("area code"))) {
    return "phone";
  }
  if (topicsLower.some((t) => t.includes("city") || t.includes("cities"))) {
    return "cities";
  }
  // Default to admin (states, provinces, regions, etc.)
  return "admin";
}

/**
 * Get category display info
 */
function getCategoryInfo(category: Category): { emoji: string; label: string } {
  switch (category) {
    case "phone":
      return { emoji: "📞", label: "Phone Area Codes" };
    case "cities":
      return { emoji: "🏙️", label: "Cities" };
    case "admin":
      return { emoji: "🗺️", label: "Administrative Divisions" };
    case "custom":
      return { emoji: "📁", label: "Custom" };
  }
}

export function Step6SaveAndPlay() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const dataset = useStudioStore(selectDataset);
  const promptKeys = useStudioStore(selectPromptKeys);
  const filters = useStudioStore(selectFilters);
  const scopeConfig = useStudioStore(selectScopeConfig);
  const excludedFeatureIds = useStudioStore((state) => state.excludedFeatureIds);
  const quizMetadata = useStudioStore((state) => ({
    title: state.title,
    description: state.description,
    category: state.category,
  }));

  // Default title to GeoJSON filename if not set
  const [title, setTitle] = useState(quizMetadata.title || dataset.label || "");
  const [description, setDescription] = useState(quizMetadata.description || "");
  const [isSaving, setIsSaving] = useState(false);

  // Auto-detect category from dataset topics
  const category = detectCategory(dataset.source?.topics);
  const categoryInfo = getCategoryInfo(category);

  // Calculate final stats (memoized to avoid recalculation on every render)
  const { filteredFeatures, finalFeatures, filteredCollection, uniquePrompts, totalQuestions } = useMemo(() => {
    const filtered = countFilteredFeatures(dataset.collection, filters);
    const final = filtered - excludedFeatureIds.length;

    // Calculate unique prompts (handles grouping by shared values)
    const collection = dataset.collection ? applyFilters(dataset.collection, filters) : null;
    const prompts = countFinalPrompts(collection, promptKeys, excludedFeatureIds);
    const questions = prompts * scopeConfig.repetitions;

    return {
      filteredFeatures: filtered,
      finalFeatures: final,
      filteredCollection: collection,
      uniquePrompts: prompts,
      totalQuestions: questions,
    };
  }, [dataset.collection, filters, excludedFeatureIds, promptKeys, scopeConfig.repetitions]);

  const canSave = title.trim().length > 0 && finalFeatures > 0 && promptKeys.length > 0;

  const handleSave = async () => {
    if (!canSave || !dataset.collection) return;

    setIsSaving(true);

    try {
      // Update metadata in store
      setSaveMetadata({
        title: title.trim(),
        description: description.trim(),
        category,
      });

      // Apply filters to get final feature collection
      const filteredCollection = applyFilters(dataset.collection, filters);

      // Remove excluded features
      // IMPORTANT: Must use original unfiltered collection to get correct indices for feature IDs
      const excludedSet = new Set(excludedFeatureIds);
      const finalFeatures = filteredCollection.features.filter((feature) => {
        // Find the original index in the unfiltered collection
        const originalIndex = dataset.collection.features.indexOf(feature);
        if (originalIndex === -1) return true; // Feature not found (shouldn't happen), include it

        const featureId = getFeatureIdentifier(feature, originalIndex);
        return !excludedSet.has(featureId);
      });

      const finalCollection = {
        ...filteredCollection,
        features: finalFeatures,
      };

      // Determine dataset source
      let datasetSource: DatasetSource | null = null;
      let storedCollection: string | undefined = undefined;

      console.log("💾 Step6: Determining dataset source:", {
        sourceType: dataset.source?.type,
        hasPath: !!dataset.source?.path,
        path: dataset.source?.path,
        hasUrl: !!dataset.source?.url,
        url: dataset.source?.url,
      });

      if (dataset.source?.type === "catalogue" && dataset.source.path) {
        // Catalogue dataset - store as dataset-url with full path
        datasetSource = {
          type: "dataset-url",
          path: dataset.source.path,
        };
        console.log("💾 Step6: Saved as dataset-url with path:", datasetSource.path);
      } else if (dataset.source?.type === "url" && dataset.source.url) {
        // URL dataset
        datasetSource = {
          type: "dataset-url",
          path: dataset.source.url,
        };
        console.log("💾 Step6: Saved as dataset-url with url:", datasetSource.path);
      } else {
        // File upload - store inline
        datasetSource = {
          type: "inline",
          id: `upload-${Date.now()}`,
        };
        storedCollection = JSON.stringify(finalCollection);
        console.log("💾 Step6: Saved as inline with id:", datasetSource.id);
      }

      // Extract geography from dataset
      // For catalogue datasets, look up in geojsonDatasets by path
      let geography = "Unknown";
      if (dataset.source?.type === "catalogue" && dataset.source.path) {
        const { geojsonDatasets } = await import("@/data/geojsonDatasets");
        const datasetInfo = geojsonDatasets.find(d => d.path === dataset.source?.path);
        geography = datasetInfo?.geography || title.trim(); // Fallback to quiz title
      } else {
        // For uploaded datasets, use the quiz title as geography
        geography = title.trim();
      }

      // Build QuizConfig
      const quizConfig = buildQuizConfig({
        title: title.trim(),
        description: description.trim(),
        category,
        geography,
        datasetLabel: dataset.label,
        datasetSource,
        promptKeys,
        scopeKey: scopeConfig.scopeKey,
        scopeBias: scopeConfig.scopeBias,
        repetitions: scopeConfig.repetitions,
        maxAttemptsPerPrompt: scopeConfig.maxAttempts,
        filters,
        excludedFeatureIds,
        totalFeatures: dataset.collection.features.length,
        eligibleCount: finalFeatures.length,
        granularityLevel: 0, // TODO: Detect from dataset
        storedCollection,
      });

      // Save to IndexedDB
      await db.quiz_configs.put(quizConfig);

      toast({
        title: "Quiz saved!",
        description: `"${title.trim()}" is ready to play.`,
      });

      // Reset wizard and navigate to quiz play
      resetWizard();
      navigate(`/quiz/play?signature=${quizConfig.signature}`);
    } catch (error) {
      console.error("Failed to save quiz:", error);
      toast({
        title: "Failed to save quiz",
        description: error instanceof Error ? error.message : "Unknown error",
        variant: "destructive",
      });
      setIsSaving(false);
    }
  };

  if (!dataset.collection) {
    return (
      <Card>
        <CardContent className="p-8 text-center text-muted-foreground">
          <p>No dataset loaded. Please go back to Step 1.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Quiz Metadata Card */}
      <Card>
        <CardHeader>
          <CardTitle>Name Your Quiz</CardTitle>
          <p className="text-sm text-muted-foreground">
            Give your quiz a memorable name and description
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Title */}
          <div className="space-y-2">
            <Label htmlFor="quiz-title">
              Title <span className="text-red-500">*</span>
            </Label>
            <Input
              id="quiz-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g., California Area Codes, European Capitals, etc."
              maxLength={100}
            />
            <p className="text-xs text-muted-foreground">
              {title.length}/100 characters
            </p>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="quiz-description">Description (Optional)</Label>
            <Textarea
              id="quiz-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Add notes about this quiz..."
              rows={3}
              maxLength={500}
            />
            <p className="text-xs text-muted-foreground">
              {description.length}/500 characters
            </p>
          </div>

          {/* Category (auto-detected) */}
          <div className="space-y-2">
            <Label>Category</Label>
            <div className="p-3 rounded-md border bg-muted/50">
              <span className="text-sm">
                {categoryInfo.emoji} {categoryInfo.label}
              </span>
            </div>
            <p className="text-xs text-muted-foreground">
              {category === "custom"
                ? "Custom dataset (uploaded/URL)"
                : "Auto-detected from dataset"}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Configuration Summary Card */}
      <Card>
        <CardHeader>
          <CardTitle>Configuration Summary</CardTitle>
          <p className="text-sm text-muted-foreground">
            Review your quiz configuration
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Dataset */}
          <div>
            <p className="text-sm font-semibold mb-2">Dataset</p>
            <Badge variant="outline">{dataset.label}</Badge>
          </div>

          {/* Prompts */}
          <div>
            <p className="text-sm font-semibold mb-2">
              Prompt Properties ({promptKeys.length})
            </p>
            <div className="flex flex-wrap gap-2">
              {promptKeys.map((key) => (
                <Badge key={key} variant="secondary">
                  {key}
                </Badge>
              ))}
            </div>
          </div>

          {/* Filters */}
          {filters.length > 0 && (
            <div>
              <p className="text-sm font-semibold mb-2">
                Filters ({filters.length})
              </p>
              <div className="space-y-1 text-xs text-muted-foreground">
                {filters.map((filter) => (
                  <div key={filter.id}>
                    • {filter.propertyKey} ({filter.mode})
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Exclusions */}
          {excludedFeatureIds.length > 0 && (
            <div>
              <p className="text-sm font-semibold mb-2">Exclusions</p>
              <p className="text-xs text-muted-foreground">
                {excludedFeatureIds.length} feature(s) manually excluded
              </p>
            </div>
          )}

          {/* Scope Settings */}
          <div>
            <p className="text-sm font-semibold mb-2">Quiz Settings</p>
            <div className="space-y-1 text-xs text-muted-foreground">
              {scopeConfig.scopeKey && (
                <div>• Scope: Grouped by {scopeConfig.scopeKey}</div>
              )}
              <div>• Repetitions: {scopeConfig.repetitions}x</div>
              <div>• Max Attempts: {scopeConfig.maxAttempts} per question</div>
            </div>
          </div>

          {/* Final Stats */}
          <div className="p-4 rounded-lg bg-primary/5 border border-primary/20">
            <p className="text-sm font-semibold mb-2">Final Quiz Stats</p>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div>
                <span className="text-muted-foreground">Features:</span>
                <span className="ml-2 font-medium">{finalFeatures}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Total Questions:</span>
                <span className="ml-2 font-medium">{totalQuestions}</span>
              </div>
            </div>
          </div>

          {/* Warning if no features */}
          {finalFeatures === 0 && (
            <div className="p-4 rounded-lg bg-destructive/10 border border-destructive/20">
              <p className="text-sm text-destructive font-semibold">
                ⚠️ No features selected
              </p>
              <p className="text-xs text-destructive/80 mt-1">
                Your filters and exclusions removed all features. Please adjust
                them to include at least one feature.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Ready to Quiz?</CardTitle>
          <p className="text-sm text-muted-foreground">
            Choose how you want to proceed
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          {/* Primary: Save & Play */}
          <Button
            onClick={handleSave}
            disabled={!canSave || isSaving}
            className="w-full"
            size="lg"
          >
            {isSaving ? (
              <>Saving...</>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save & Start Quiz
              </>
            )}
          </Button>
          <p className="text-xs text-center text-muted-foreground">
            Saves quiz to "My Quizzes" and starts playing immediately
          </p>

        </CardContent>
      </Card>
    </div>
  );
}
